// ---------------------------------------------------------------------------
// The platform's read-cache FRESHNESS policy. One table, read by the
// node-backend read cache that enforces it and by every limit derived from it
// (a Source's default and longest freshness, which the editor validates).
// ---------------------------------------------------------------------------

/** The read families that carry their own freshness policy. */
export type AgenticLogicReadKind =
  /** Storefront catalog reads: `storefrontQuery`, `productRecommendations`. */
  | "storefront"
  /** Admin ID-selector catalog reads (`inStock`, `newArrivals`, ...). */
  | "adminCatalog"
  /** The ShopifyQL best-seller rank. */
  | "shopifyqlRank"
  /** The shop's currency, read under a legacy Admin product query. */
  | "shopCurrency"
  /** The app's granted access scopes, read under the ShopifyQL rank. */
  | "shopScopes"
  /** One shopper's PII-minimal customer projection. */
  | "customer"
  /**
   * The value a `script` Source built. Built on a schedule, never on a read,
   * so it is kept a week: a failed build serves last-known-good until the
   * next one lands.
   */
  | "sourceScript"
  /**
   * The shop's Shopify delivery zones and their rates, swept for the
   * shipping-context threshold. Merchants rework zones a handful of times a
   * year, so it is served for an hour and kept a week: a failed sweep serves
   * last-known-good until the next one lands.
   */
  | "shippingZones";

export interface AgenticLogicReadFreshness {
  /** Soft TTL: younger entries are served without a refresh. */
  freshMs: number;
  /** Hard TTL: older entries are gone. `maxStaleMs === freshMs` means "never stale". */
  maxStaleMs: number;
}

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/**
 * THE platform freshness table. Catalog-shaped reads may serve a day-old
 * answer while one refresh runs, because availability is re-checked live on
 * the winners. The shop's scopes are never served stale: a scope grant must
 * take effect within one fresh window. A customer is personal and changes
 * with every order, so it is short and never stale. Product costs are not
 * here — node-backend's product-costs service owns its own 15-minute lane —
 * and `checkAvailability` bypasses the cache entirely.
 */
export const AGENTIC_LOGIC_READ_FRESHNESS: Readonly<
  Record<AgenticLogicReadKind, AgenticLogicReadFreshness>
> = {
  storefront: { freshMs: 10 * MINUTE_MS, maxStaleMs: DAY_MS },
  adminCatalog: { freshMs: 10 * MINUTE_MS, maxStaleMs: DAY_MS },
  shopifyqlRank: { freshMs: 10 * MINUTE_MS, maxStaleMs: DAY_MS },
  shopCurrency: { freshMs: 10 * MINUTE_MS, maxStaleMs: DAY_MS },
  shopScopes: { freshMs: 10 * MINUTE_MS, maxStaleMs: 10 * MINUTE_MS },
  customer: { freshMs: MINUTE_MS, maxStaleMs: MINUTE_MS },
  sourceScript: { freshMs: 60 * MINUTE_MS, maxStaleMs: 7 * DAY_MS },
  shippingZones: { freshMs: 60 * MINUTE_MS, maxStaleMs: 7 * DAY_MS },
};

/**
 * The longest any entry is kept. The read cache's per-org generation marker
 * must outlive every entry it stamps, so it is derived from this rather than
 * restated beside it.
 */
export const AGENTIC_LOGIC_READ_MAX_STALE_MS = Math.max(
  ...Object.values(AGENTIC_LOGIC_READ_FRESHNESS).map(
    (freshness) => freshness.maxStaleMs
  )
);

/**
 * Every {@link AgenticLogicReadKind}, for the schemas that carry a kind over
 * the wire (the read-cache telemetry). Keyed off the freshness table so a new
 * kind cannot be added to one and forgotten in the other.
 */
export const AgenticLogicReadKindList = Object.keys(
  AGENTIC_LOGIC_READ_FRESHNESS
) as [AgenticLogicReadKind, ...AgenticLogicReadKind[]];
